// Dependency-free canvas confetti burst. One transient full-screen canvas is
// created per burst, animated with requestAnimationFrame, then removed.
// Tuned to feel celebratory but tasteful — brand violets + mint + gold.

const COLORS = ["#a855f7", "#c084fc", "#34d399", "#fbbf24", "#f472b6", "#60a5fa", "#ffffff"];

export function celebrate({ x = 0.5, y = 0.35, count = 130, spread = 78 } = {}) {
  if (typeof window === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999";
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const resize = () => {
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();

  const W = window.innerWidth;
  const H = window.innerHeight;
  const originX = W * x;
  const originY = H * y;
  const gravity = 0.32;
  const drag = 0.992;

  const particles = Array.from({ length: count }).map(() => {
    const angle = (-90 + (Math.random() - 0.5) * spread) * (Math.PI / 180);
    const velocity = 9 + Math.random() * 12;
    return {
      x: originX,
      y: originY,
      vx: Math.cos(angle) * velocity + (Math.random() - 0.5) * 3,
      vy: Math.sin(angle) * velocity,
      size: 5 + Math.random() * 7,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: COLORS[(Math.random() * COLORS.length) | 0],
      shape: Math.random() > 0.5 ? "rect" : "circle",
      life: 0,
      ttl: 120 + Math.random() * 60,
    };
  });

  let frame = 0;
  const maxFrames = 220;

  const tick = () => {
    ctx.clearRect(0, 0, W, H);
    let alive = 0;
    for (const p of particles) {
      p.life += 1;
      if (p.life > p.ttl) continue;
      alive += 1;
      p.vx *= drag;
      p.vy = p.vy * drag + gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      const fade = Math.max(0, 1 - p.life / p.ttl);
      ctx.globalAlpha = fade;
      ctx.fillStyle = p.color;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      if (p.shape === "rect") {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    frame += 1;
    if (alive > 0 && frame < maxFrames) {
      requestAnimationFrame(tick);
    } else {
      canvas.remove();
    }
  };
  requestAnimationFrame(tick);
}
